import React from 'react';
import { X, MessageCircle, Mail, User, Package, Tag, TrendingDown } from 'lucide-react';

function ComboDetailModal({ combo, onClose }) {
  function handleWhatsApp() {
    const phone = combo.seller.phone_number.replace(/\D/g, '');
    const message = encodeURIComponent(
      `Hola! Estoy interesado en el combo: ${combo.name} (SKU: ${combo.sku})`
    );
    window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-primary via-brand-gold to-brand-green text-white p-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold font-montserrat">Detalles del Combo</h2>
              <p className="text-xs opacity-90 font-montserrat">{combo.sku}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left: Combo Details */}
            <div className="space-y-4">
              {/* Combo Name */}
              <div>
                <div className="flex items-start gap-3 mb-2">
                  <h3 className="text-2xl font-bold text-slate-800 font-montserrat flex-1">
                    {combo.name}
                  </h3>
                  {combo.discount_percentage > 0 && (
                    <div className="bg-red-500 text-white px-3 py-1 rounded-full flex items-center gap-1.5">
                      <TrendingDown className="w-4 h-4" />
                      <span className="text-sm font-bold font-montserrat">
                        -{combo.discount_percentage}% OFF
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {combo.description && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-slate-700 font-montserrat mb-2">
                    Descripción
                  </h4>
                  <p className="text-sm text-slate-600 font-montserrat leading-relaxed whitespace-pre-line">
                    {combo.description}
                  </p>
                </div>
              )}

              {/* Pricing Section */}
              <div className="bg-gradient-to-br from-brand-primary/10 to-brand-gold/10 rounded-lg p-4 border-2 border-brand-primary/20">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs text-slate-600 font-montserrat mb-1">Precio Regular</p>
                    <p className="text-lg text-slate-500 line-through font-montserrat">
                      S/ {combo.regular_price.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-green-700 font-montserrat mb-1 font-semibold">
                      Ahorras
                    </p>
                    <p className="text-lg font-bold text-green-600 font-montserrat">
                      S/ {combo.savings.toFixed(2)}
                    </p>
                  </div>
                </div>
                
                <div className="pt-3 border-t border-slate-300/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Tag className="w-5 h-5 text-brand-gold" />
                      <span className="text-sm text-slate-700 font-montserrat">Precio del Combo</span>
                    </div>
                    <p className="text-3xl font-bold text-brand-primary font-montserrat">
                      S/ {combo.combo_price.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Products Included */}
              <div>
                <h4 className="text-lg font-bold text-slate-800 font-montserrat mb-3">
                  Productos Incluidos ({combo.total_items} items)
                </h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {combo.items?.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-3 hover:border-brand-primary/30 transition-all"
                    >
                      <img
                        src={item.product.primary_image || '/placeholder-image.png'}
                        alt={item.product.title}
                        className="w-16 h-16 object-cover rounded-lg bg-slate-100"
                        onError={(e) => {
                          e.target.src = '/placeholder-image.png';
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 font-montserrat text-sm truncate">
                          {item.product.title}
                        </p>
                        <p className="text-xs text-slate-500 font-montserrat">
                          {item.product.sku}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-slate-500 font-montserrat">Cantidad</p>
                        <p className="text-lg font-bold text-brand-primary font-montserrat">
                          {item.quantity}x
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Provider Info */}
            <div className="space-y-4">
              {/* Provider Card */}
              <div className="bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-xl p-5 text-white sticky top-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm opacity-90 font-montserrat">Proveedor</p>
                    <h3 className="text-xl font-bold font-montserrat truncate">
                      {combo.seller.username}
                    </h3>
                  </div>
                </div>

                <div className="space-y-2">
                  {/* Email */}
                  {combo.seller.email && (
                    <div className="flex items-start gap-2 bg-white/10 rounded-lg p-3">
                      <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs opacity-75 font-montserrat mb-0.5">Email</p>
                        <a 
                          href={`mailto:${combo.seller.email}`}
                          className="text-sm font-semibold hover:underline font-montserrat break-all"
                        >
                          {combo.seller.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {/* WhatsApp */}
                  <div className="flex items-start gap-2 bg-white/10 rounded-lg p-3">
                    <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs opacity-75 font-montserrat mb-0.5">WhatsApp</p>
                      <p className="text-sm font-semibold font-montserrat">
                        {combo.seller.phone_number}
                      </p>
                    </div>
                  </div>
                </div>

                {/* WhatsApp Button */}
                <button
                  onClick={handleWhatsApp}
                  className="w-full mt-4 flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-3 rounded-lg transition-all font-montserrat font-semibold shadow-md hover:shadow-lg"
                >
                  <MessageCircle className="w-5 h-5" />
                  Consultar por WhatsApp
                </button>
              </div>

              {/* Additional Info */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="text-sm font-bold text-slate-800 font-montserrat mb-3">
                  Información Importante
                </h4>
                <div className="space-y-2 text-xs text-slate-600 font-montserrat">
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-1 flex-shrink-0" />
                    <p>Este combo incluye {combo.total_items} productos seleccionados</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-1 flex-shrink-0" />
                    <p>Ahorras S/ {combo.savings.toFixed(2)} comprando el combo completo</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-1 flex-shrink-0" />
                    <p>Contacta directamente al proveedor para realizar tu pedido</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full mt-1 flex-shrink-0" />
                    <p>Los combos son manejados exclusivamente por el proveedor</p>
                  </div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-white border-2 border-slate-200 rounded-lg p-2 text-center">
                  <div className="text-xl mb-0.5">🎁</div>
                  <p className="text-[10px] font-semibold text-slate-700 font-montserrat leading-tight">Combo Especial</p>
                </div>
                <div className="bg-white border-2 border-slate-200 rounded-lg p-2 text-center">
                  <div className="text-xl mb-0.5">✓</div>
                  <p className="text-[10px] font-semibold text-slate-700 font-montserrat leading-tight">Verificado</p>
                </div>
                <div className="bg-white border-2 border-slate-200 rounded-lg p-2 text-center">
                  <div className="text-xl mb-0.5">💰</div>
                  <p className="text-[10px] font-semibold text-slate-700 font-montserrat leading-tight">Mejor Precio</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ComboDetailModal;